import { MutableRefObject, useEffect, useRef } from 'react';
import { debounce, DebouncedFunc } from 'lodash-es';
import useWindow from './useWindow';

const useResizeScroll = <Element extends HTMLElement>(config: {
  resizeCb?: (container?: Element | null) => void;
  scrollCb?: (container?: Element | null) => void;
  debounceTime?: number;
  deps?: unknown[];
  updateOnResize?: boolean;
}): MutableRefObject<Element | null> => {
  const window = useWindow();
  const { resizeCb, debounceTime = 200, deps, scrollCb, updateOnResize } = config;
  const containerRef = useRef<Element>(null);

  useEffect(() => {
    setTimeout(() => {
      const containerEl = containerRef?.current;
      let debouncedResizeCb: DebouncedFunc<() => void> | undefined;
      let debouncedScrollCb: DebouncedFunc<() => void> | undefined;
      if (scrollCb) {
        debouncedScrollCb = debounce(() => {
          scrollCb(containerEl);
        }, debounceTime);
      }
      if (resizeCb || updateOnResize) {
        debouncedResizeCb = debounce(() => {
          if (resizeCb) {
            resizeCb(containerEl);
          }
          if (debouncedScrollCb) {
            containerEl?.removeEventListener('scroll', debouncedScrollCb);
            containerEl?.addEventListener('scroll', debouncedScrollCb);
          }
        }, debounceTime);

        window?.removeEventListener('resize', debouncedResizeCb);
        window?.addEventListener('resize', debouncedResizeCb);
        if (resizeCb) {
          resizeCb(containerEl);
        }
      }

      if (debouncedScrollCb && scrollCb) {
        containerEl?.removeEventListener('scroll', debouncedScrollCb);
        containerEl?.addEventListener('scroll', debouncedScrollCb);
        scrollCb(containerEl);
      }

      return (): void => {
        if (debouncedResizeCb) {
          debouncedResizeCb.cancel();
          window?.removeEventListener('resize', debouncedResizeCb);
        }
        if (debouncedScrollCb) {
          debouncedScrollCb.cancel();
          containerEl?.removeEventListener('scroll', debouncedScrollCb);
        }
      };
    });

    // Dependencies have to be spreadable or useEffect won't know when to update correctly
    // eslint-disable-next-line
  }, [containerRef, debounceTime, scrollCb, resizeCb, ...(deps || [])]);

  return containerRef;
};

export default useResizeScroll;
